import { scanArtifactForSecrets } from "@mph/artifact-scan";
import {
  HUGO_CONFIG_FILENAMES,
  HUGO_STATIC_OUTPUT_CONTRACT,
  staticOutputContractForPackages,
  type StaticOutputContractId,
} from "@mph/build-contracts/catalog";
import { buildProjectManifest } from "@mph/project-manifest";
import {
  CodedError,
  type FileData,
  type ProjectSnapshot,
  type SnapshotFile,
  type StaticPublishDirectory,
  byteLength,
  fileText,
  stableRecordEntries,
} from "@mph/shared";
import type { ApiDiscoveryFinding, ApiDiscoveryResult } from "./api-discovery";

export * from "./runtime-client-template";
export type * from "./api-discovery";
export { HUGO_CONFIG_FILENAMES };

export type SourceBuildFramework =
  | "vite"
  | "astro-static"
  | "create-react-app"
  | "next-static-export"
  | "react-router-spa"
  | "hugo";

export type NodeSourcePackageManager = "npm" | "pnpm";
export type SourcePackageManager = NodeSourcePackageManager | "hugo";

export interface HugoBuildPlanProvenance {
  builderContractVersion: "hugo-v1";
  hugoVersion: "0.164.0";
  selectedConfig: typeof HUGO_CONFIG_FILENAMES[number];
  baseUrlPolicy: "root-relative";
  environment: "production";
  includeDrafts: false;
  includeFuture: false;
  includeExpired: false;
  minify: true;
}

export interface HugoBuildProvenance extends HugoBuildPlanProvenance {
  sourceDigest: string;
}

export type SourceRuntimeCredential = "bearer" | "basic-empty-password" | "header" | "query" | "webhook";

export interface SourceRuntimeActionPolicy {
  method: string;
  origin: string;
  path: string;
  query: string[];
  headers: string[];
  body?: {
    mode: "json" | "text";
    fields: string[];
  };
}

export interface SourceRuntimeConnection {
  alias: string;
  label: string;
  environmentVariable: string;
  credential: SourceRuntimeCredential;
  credentialName?: string;
  hosts: string[];
  actions?: SourceRuntimeActionPolicy[];
}

export interface SourceRuntimeAccessAdapter {
  type: "signed-cookie";
  cookieName: string;
  passwordEnvironmentVariable: string;
  sessionSecretEnvironmentVariable: string;
}

export interface SourceRuntimePlan {
  supported: true;
  framework: "next-managed-runtime";
  packageManager: NodeSourcePackageManager;
  installCommand: string;
  buildCommand: string;
  startCommand: string;
  port: 8080;
  connections: SourceRuntimeConnection[];
  buildHosts: string[];
  access?: SourceRuntimeAccessAdapter;
}

export type SourceRuntimeBlockCode =
  | SourceBuildBlockCode
  | "SOURCE_RUNTIME_CONNECTION_UNSUPPORTED"
  | "SOURCE_RUNTIME_NO_CONNECTIONS";

export interface BlockedSourceRuntimePlan {
  supported: false;
  code: SourceRuntimeBlockCode;
  message: string;
}

export type SourceRuntimeDetection = SourceRuntimePlan | BlockedSourceRuntimePlan;

export interface SourceBuildPlan {
  supported: true;
  framework: SourceBuildFramework;
  packageManager: SourcePackageManager;
  installCommand: string;
  buildCommand: string;
  publishDirectory: Exclude<StaticPublishDirectory, ".">;
  provenance?: HugoBuildPlanProvenance;
}

export type SourceBuildBlockCode =
  | "SOURCE_BUILD_AMBIGUOUS_LOCKFILE"
  | "SOURCE_BUILD_CONFIG_UNSUPPORTED"
  | "SOURCE_BUILD_FRAMEWORK_UNSUPPORTED"
  | "SOURCE_BUILD_LOCKFILE_REQUIRED"
  | "SOURCE_BUILD_MALFORMED_PACKAGE_JSON"
  | "SOURCE_BUILD_PACKAGE_MANAGER_UNSUPPORTED"
  | "SOURCE_BUILD_SERVER_RUNTIME";

export interface BlockedSourceBuildPlan {
  supported: false;
  code: SourceBuildBlockCode;
  message: string;
}

export type SourceBuildDetection = SourceBuildPlan | BlockedSourceBuildPlan;

export type SourceUploadCode =
  | "SOURCE_FILE_TOO_LARGE"
  | "SECRET_DETECTED"
  | "SOURCE_UNSAFE_FILE_TYPE";

export class SourceBuildError<Code extends string = string> extends CodedError<Code> {
  constructor(code: Code, message: string) {
    super(code, message);
    this.name = "SourceBuildError";
  }
}

interface PackageJson {
  dependencies?: Record<string, unknown>;
  devDependencies?: Record<string, unknown>;
  peerDependencies?: Record<string, unknown>;
}

interface RuntimeConnectionCatalogEntry {
  alias: string;
  label: string;
  credential: SourceRuntimeCredential;
  hosts: string[];
}

const ENABLED_FRAMEWORKS = new Set<StaticOutputContractId>([
  "vite",
  "astro-static",
  "create-react-app",
  "next-static-export",
  "react-router-spa",
]);

type NodeSourceBuildFramework = Exclude<SourceBuildFramework, "hugo">;

const BUILD_COMMANDS: Record<NodeSourceBuildFramework, string> = {
  "vite": "./node_modules/.bin/vite build",
  "astro-static": "./node_modules/.bin/astro build",
  "create-react-app": "./node_modules/.bin/react-scripts build",
  "next-static-export": "./node_modules/.bin/next build",
  "react-router-spa": "./node_modules/.bin/react-router build",
};

const INSTALL_COMMANDS: Record<NodeSourcePackageManager, string> = {
  npm: "npm ci --ignore-scripts --no-audit --no-fund",
  pnpm: "pnpm install --frozen-lockfile --ignore-scripts --config.ignore-scripts=true",
};

const RUNTIME_CONNECTION_CATALOG: Readonly<Record<string, RuntimeConnectionCatalogEntry>> = {
  HUBSPOT_ACCESS_TOKEN: {
    alias: "hubspot",
    label: "HubSpot access token",
    credential: "bearer",
    hosts: ["api.hubapi.com"],
  },
  CUSTOMERIO_PIPELINE_API_KEY: {
    alias: "customerio-pipeline",
    label: "Customer.io Pipeline API key",
    credential: "basic-empty-password",
    hosts: ["cdp.customer.io"],
  },
  CUSTOMERIO_APP_API_KEY: {
    alias: "customerio-app",
    label: "Customer.io App API key",
    credential: "bearer",
    hosts: ["api.customer.io"],
  },
  FEATUREBASE_API_KEY: {
    alias: "featurebase",
    label: "Featurebase API key",
    credential: "bearer",
    hosts: ["do.featurebase.app"],
  },
  SLACK_WEBHOOK_URL: {
    alias: "slack",
    label: "Slack webhook URL",
    credential: "webhook",
    hosts: ["hooks.slack.com"],
  },
};

const DOORWAY_MANAGED_ENVIRONMENT = new Set([
  "APP_PASSWORD",
  "CRON_SECRET",
  "NODE_ENV",
  "SESSION_SECRET",
]);

const OPTIONAL_CONFIGURATION_ENVIRONMENT = new Set([
  "CUSTOMERIO_UNSUBSCRIBED_SEGMENT_ID",
]);

const RUNTIME_STRIPPED_HEADERS = new Set([
  "accept",
  "accept-encoding",
  "accept-language",
  "content-length",
  "host",
  "sec-fetch-mode",
  "user-agent",
]);

function blocked(code: SourceBuildBlockCode, message: string): BlockedSourceBuildPlan {
  return { supported: false, code, message };
}

function hasConfig(snapshot: ProjectSnapshot, base: string, expression: RegExp): boolean {
  return stableRecordEntries(snapshot).some(([path, data]) => (
    new RegExp(`^${base.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\.config(?:\\.[^/]+)?$`).test(path)
    && expression.test(fileText(data))
  ));
}

function packageManager(snapshot: ProjectSnapshot): NodeSourcePackageManager | BlockedSourceBuildPlan {
  const npm = snapshot["package-lock.json"] !== undefined;
  const pnpm = snapshot["pnpm-lock.yaml"] !== undefined;
  if (snapshot["yarn.lock"] !== undefined || snapshot["bun.lock"] !== undefined || snapshot["bun.lockb"] !== undefined) {
    return blocked(
      "SOURCE_BUILD_PACKAGE_MANAGER_UNSUPPORTED",
      "Sandbox builds currently accept npm or pnpm lockfiles.",
    );
  }
  if (npm && pnpm) {
    return blocked(
      "SOURCE_BUILD_AMBIGUOUS_LOCKFILE",
      "Keep one package manager lockfile before asking Doorway to build this project.",
    );
  }
  if (!npm && !pnpm) {
    return blocked(
      "SOURCE_BUILD_LOCKFILE_REQUIRED",
      "Commit a package-lock.json or pnpm-lock.yaml so Doorway can reproduce this build.",
    );
  }
  return npm ? "npm" : "pnpm";
}

function sourceFramework(id: StaticOutputContractId): NodeSourceBuildFramework | undefined {
  return ENABLED_FRAMEWORKS.has(id) ? id as NodeSourceBuildFramework : undefined;
}

function hugoConfigFiles(snapshot: ProjectSnapshot): Array<typeof HUGO_CONFIG_FILENAMES[number]> {
  return HUGO_CONFIG_FILENAMES.filter((path) => snapshot[path] !== undefined);
}

function hugoBlocked(message: string): BlockedSourceBuildPlan {
  return blocked("SOURCE_BUILD_CONFIG_UNSUPPORTED", message);
}

function hugoThemes(
  configPath: typeof HUGO_CONFIG_FILENAMES[number],
  config: string,
): string[] | BlockedSourceBuildPlan {
  if (configPath.endsWith(".json")) {
    try {
      const value = (JSON.parse(config) as { theme?: unknown }).theme;
      if (value === undefined) return [];
      const themes = Array.isArray(value) ? value : [value];
      if (themes.some((theme) => typeof theme !== "string" || theme.length === 0)) {
        return hugoBlocked("Hugo themes must be declared as fixed local names.");
      }
      if (themes.some((theme) => !/^[A-Za-z0-9._-]+$/.test(theme as string) || theme === "." || theme === "..")) {
        return hugoBlocked("Hugo theme names cannot escape the local themes directory.");
      }
      return themes as string[];
    } catch {
      return hugoBlocked("Doorway could not parse the Hugo JSON configuration.");
    }
  }

  const line = config.split(/\r?\n/).find((candidate) => /^\s*(?:["']theme["']|theme)\s*[:=]/i.test(candidate));
  if (!line) return [];
  const separator = line.search(/[:=]/);
  const declaration = line.slice(separator + 1);
  const quoted = [...declaration.matchAll(/["']([^"']+)["']/g)].map((match) => match[1]!).filter(Boolean);
  const values = quoted.length > 0
    ? quoted
    : declaration.replace(/[\[\]]/g, "").split(",").map((value) => value.trim()).filter(Boolean);
  if (values.length === 0) return hugoBlocked("Hugo themes must be declared as fixed local names.");
  if (values.some((value) => !/^[A-Za-z0-9._-]+$/.test(value) || value === "." || value === "..")) {
    return hugoBlocked("Hugo theme names cannot escape the local themes directory.");
  }
  return values;
}

function hugoMountSources(
  configPath: typeof HUGO_CONFIG_FILENAMES[number],
  config: string,
): string[] | BlockedSourceBuildPlan {
  if (configPath.endsWith(".json")) {
    try {
      const sources: string[] = [];
      const visit = (value: unknown): void => {
        if (Array.isArray(value)) {
          for (const entry of value) visit(entry);
          return;
        }
        if (!value || typeof value !== "object") return;
        for (const [key, entry] of Object.entries(value)) {
          if (key.toLowerCase() === "source") {
            if (typeof entry !== "string" || entry.length === 0) throw new Error("invalid Hugo mount source");
            sources.push(entry);
          }
          visit(entry);
        }
      };
      visit(JSON.parse(config));
      return sources;
    } catch {
      return hugoBlocked("Doorway could not safely parse Hugo JSON mount configuration.");
    }
  }

  const sources = [...config.matchAll(/(?:^|[,{\s])["']?source["']?\s*[:=]\s*(?:["']([^"']+)["']|([^\s,}\]]+))/gim)]
    .map((match) => (match[1] ?? match[2] ?? "").trim())
    .filter(Boolean);
  if (/\bmounts?\b/i.test(config) && sources.length === 0) {
    return hugoBlocked("Hugo module mounts must use fixed local source paths that Doorway can verify.");
  }
  return sources;
}

function hugoMountEscapesProject(source: string): boolean {
  if (!/^[A-Za-z0-9._ /-]+$/.test(source)) return true;
  if (source.startsWith("/") || source.startsWith("\\") || /^[A-Za-z]:[\\/]/.test(source)) return true;
  if (source.includes("\\") || /^[a-z][a-z0-9+.-]*:/i.test(source)) return true;
  let depth = 0;
  for (const segment of source.split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") {
      if (depth === 0) return true;
      depth -= 1;
    } else {
      depth += 1;
    }
  }
  return false;
}

function hugoBuild(snapshot: ProjectSnapshot, configs: Array<typeof HUGO_CONFIG_FILENAMES[number]>): SourceBuildDetection {
  if (configs.length !== 1) {
    return hugoBlocked("Keep exactly one supported Hugo configuration file at the project root.");
  }
  const selectedConfig = configs[0]!;
  const config = fileText(snapshot[selectedConfig]!);
  const entries = stableRecordEntries(snapshot);
  if (entries.some(([path]) => path === ".gitmodules" || path === ".git" || path.startsWith(".git/") || path.includes("/.git/"))) {
    return hugoBlocked("Hugo source builds do not accept Git metadata or theme submodules.");
  }
  if (/\benableGitInfo\b\s*[:=]\s*true/i.test(config)
    || entries.some(([, data]) => /\.GitInfo\b|\.GitHash\b/.test(fileText(data)))) {
    return hugoBlocked("Hugo source builds cannot depend on Git metadata.");
  }
  if (/security(?:\.|\s*[:=][\s\S]{0,160}\b)exec/i.test(config)
    || entries.some(([, data]) => /{{[^}]*\b(?:os\/exec|exec)\b/i.test(fileText(data)))) {
    return hugoBlocked("Hugo source builds do not accept custom executable commands.");
  }
  const mountSources = hugoMountSources(selectedConfig, config);
  if (!Array.isArray(mountSources)) return mountSources;
  if (mountSources.some(hugoMountEscapesProject)) {
    return hugoBlocked("Hugo module mounts must remain inside the uploaded project.");
  }

  const usesModuleImports = /\[\[?\s*module\.imports\s*\]?\]/i.test(config)
    || /^\s*imports\s*:/im.test(config)
    || /["']imports["']\s*:/i.test(config)
    || snapshot["go.mod"] !== undefined
    || snapshot["go.sum"] !== undefined;
  const hasVendoredModule = snapshot["_vendor/modules.txt"] !== undefined
    && entries.some(([path]) => path.startsWith("_vendor/") && path !== "_vendor/modules.txt");
  if (usesModuleImports && !hasVendoredModule) {
    return hugoBlocked("Hugo modules must be committed under _vendor before Doorway can build them.");
  }

  if (entries.some(([, data]) => (
    /resources\.GetRemote\b/i.test(fileText(data))
    || /\bget(?:JSON|CSV)\b[^\n}]{0,160}https?:\/\//i.test(fileText(data))
  ))) {
    return hugoBlocked("Hugo source builds cannot fetch remote resources.");
  }

  const themes = hugoThemes(selectedConfig, config);
  if (!Array.isArray(themes)) return themes;
  for (const theme of themes) {
    if (!entries.some(([path]) => path.startsWith(`themes/${theme}/`))) {
      return hugoBlocked(`Commit the local themes/${theme} files before Doorway builds this project.`);
    }
  }

  const buildCommand = `hugo --config ${selectedConfig} --destination public --cleanDestinationDir --environment production --baseURL / --minify --buildDrafts=false --buildFuture=false --buildExpired=false`;
  return {
    supported: true,
    framework: "hugo",
    packageManager: "hugo",
    installCommand: "",
    buildCommand,
    publishDirectory: HUGO_STATIC_OUTPUT_CONTRACT.publishDirectory,
    provenance: {
      builderContractVersion: "hugo-v1",
      hugoVersion: "0.164.0",
      selectedConfig,
      baseUrlPolicy: "root-relative",
      environment: "production",
      includeDrafts: false,
      includeFuture: false,
      includeExpired: false,
      minify: true,
    },
  };
}

export function detectSourceBuild(snapshot: ProjectSnapshot): SourceBuildDetection {
  const configs = hugoConfigFiles(snapshot);
  if (configs.length > 0) return hugoBuild(snapshot, configs);
  const packageData = snapshot["package.json"];
  if (packageData === undefined) {
    return blocked("SOURCE_BUILD_FRAMEWORK_UNSUPPORTED", "A sandbox source build requires package.json.");
  }
  const unsafeConfig = [".npmrc", ".pnpmfile.cjs", ".pnpmfile.js", "pnpm-workspace.yaml"]
    .find((path) => snapshot[path] !== undefined);
  if (unsafeConfig) {
    return blocked(
      "SOURCE_BUILD_CONFIG_UNSUPPORTED",
      `${unsafeConfig} is not accepted by Doorway source builds. Use a single root package with the standard npm registry.`,
    );
  }

  let packageJson: PackageJson;
  try {
    const parsed: unknown = JSON.parse(fileText(packageData));
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) throw new Error("invalid");
    packageJson = parsed as PackageJson;
  } catch {
    return blocked("SOURCE_BUILD_MALFORMED_PACKAGE_JSON", "package.json must be valid JSON.");
  }

  const names = new Set(Object.keys({
    ...packageJson.peerDependencies,
    ...packageJson.devDependencies,
    ...packageJson.dependencies,
  }));
  const contract = staticOutputContractForPackages(names);
  const framework = contract ? sourceFramework(contract.id) : undefined;
  if (!contract || !framework) {
    return blocked(
      "SOURCE_BUILD_FRAMEWORK_UNSUPPORTED",
      "This framework does not have an enabled Doorway source builder yet.",
    );
  }

  if (framework === "astro-static" && hasConfig(snapshot, "astro", /output\s*:\s*["']server["']/)) {
    return blocked("SOURCE_BUILD_SERVER_RUNTIME", "This Astro project is configured for server output.");
  }
  if (
    framework === "next-static-export"
    && !hasConfig(snapshot, "next", /output\s*:\s*["']export["']/)
  ) {
    return blocked(
      "SOURCE_BUILD_SERVER_RUNTIME",
      "Next.js sandbox builds require output: 'export' in next.config.",
    );
  }
  if (
    framework === "react-router-spa"
    && !hasConfig(snapshot, "react-router", /ssr\s*:\s*false/)
  ) {
    return blocked(
      "SOURCE_BUILD_SERVER_RUNTIME",
      "React Router sandbox builds require ssr: false in react-router.config.",
    );
  }

  const manager = packageManager(snapshot);
  if (typeof manager !== "string") return manager;
  return {
    supported: true,
    framework,
    packageManager: manager,
    installCommand: INSTALL_COMMANDS[manager],
    buildCommand: BUILD_COMMANDS[framework],
    publishDirectory: contract.publishDirectory,
  };
}

export function detectSourceRuntime(
  snapshot: ProjectSnapshot,
  discovery?: ApiDiscoveryResult,
): SourceRuntimeDetection {
  const packageData = snapshot["package.json"];
  if (packageData === undefined) {
    return blockedRuntime("SOURCE_BUILD_FRAMEWORK_UNSUPPORTED", "A managed runtime requires package.json.");
  }
  if ([".npmrc", ".pnpmfile.cjs", ".pnpmfile.js", "pnpm-workspace.yaml"].some((path) => snapshot[path] !== undefined)) {
    return blockedRuntime(
      "SOURCE_BUILD_CONFIG_UNSUPPORTED",
      "Managed runtimes currently require a single root package and the standard npm registry.",
    );
  }

  let parsed: PackageJson;
  try {
    const value: unknown = JSON.parse(fileText(packageData));
    if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error("invalid");
    parsed = value as PackageJson;
  } catch {
    return blockedRuntime("SOURCE_BUILD_MALFORMED_PACKAGE_JSON", "package.json must be valid JSON.");
  }
  const dependencies = new Set(Object.keys({
    ...parsed.peerDependencies,
    ...parsed.devDependencies,
    ...parsed.dependencies,
  }));
  if (!dependencies.has("next") || hasConfig(snapshot, "next", /output\s*:\s*["']export["']/)) {
    return blockedRuntime(
      "SOURCE_BUILD_FRAMEWORK_UNSUPPORTED",
      "This project does not use an enabled managed server runtime.",
    );
  }
  const manager = packageManager(snapshot);
  if (typeof manager !== "string") return blockedRuntime(manager.code, manager.message);

  const environment = referencedEnvironment(snapshot);
  if (!discovery) {
    return blockedRuntime(
      "SOURCE_RUNTIME_CONNECTION_UNSUPPORTED",
      "Doorway must complete isolated API discovery before preparing this server runtime.",
    );
  }
  if (!discovery.complete || discovery.blockers.length > 0) {
    return blockedRuntime(
      "SOURCE_RUNTIME_CONNECTION_UNSUPPORTED",
      "Doorway could not resolve every server API boundary safely.",
    );
  }
  const discoveredConnections = runtimeConnectionsFromDiscovery(discovery);
  const discoveredEnvironment = new Set(discoveredConnections.map((connection) => connection.environmentVariable));
  const observedCredentialEnvironment = new Set(discovery.credentialSources
    .filter((source) => source.locus === "server" && source.confidence === "high")
    .map((source) => source.source));
  const unsupported = [...environment]
    .filter((name) => !discoveredEnvironment.has(name)
      && !(RUNTIME_CONNECTION_CATALOG[name] && observedCredentialEnvironment.has(name)))
    .filter((name) => !DOORWAY_MANAGED_ENVIRONMENT.has(name))
    .filter((name) => !OPTIONAL_CONFIGURATION_ENVIRONMENT.has(name))
    .filter((name) => !name.startsWith("NEXT_PUBLIC_"))
    .sort();
  if (unsupported.length > 0) {
    return blockedRuntime(
      "SOURCE_RUNTIME_CONNECTION_UNSUPPORTED",
      `Doorway needs a connection recipe for: ${unsupported.join(", ")}.`,
    );
  }
  const hintedConnections = Object.entries(RUNTIME_CONNECTION_CATALOG)
    .filter(([name]) => environment.has(name) && observedCredentialEnvironment.has(name) && !discoveredEnvironment.has(name))
    .map(([environmentVariable, entry]) => ({ ...entry, environmentVariable, actions: [] }));
  const connectionByEnvironment = new Map([...discoveredConnections, ...hintedConnections]
    .map((connection) => [connection.environmentVariable, connection]));
  const connections = [
    ...Object.keys(RUNTIME_CONNECTION_CATALOG).flatMap((name) => {
      const connection = connectionByEnvironment.get(name);
      return connection ? [connection] : [];
    }),
    ...[...connectionByEnvironment.values()]
      .filter((connection) => !RUNTIME_CONNECTION_CATALOG[connection.environmentVariable])
      .sort((left, right) => left.environmentVariable.localeCompare(right.environmentVariable)),
  ];
  if (connections.length > 16 || new Set(connections.map((connection) => connection.alias)).size !== connections.length
    || connections.some((connection) => (connection.actions?.length ?? 0) > 64)) {
    return blockedRuntime(
      "SOURCE_RUNTIME_CONNECTION_UNSUPPORTED",
      "The discovered server connection policy exceeds the managed runtime limits.",
    );
  }
  if (connections.length === 0) {
    return blockedRuntime(
      "SOURCE_RUNTIME_NO_CONNECTIONS",
      "This server app does not expose a supported external API connection.",
    );
  }

  const access = detectSignedCookieAccess(snapshot, environment);
  const usesGoogleFonts = stableRecordEntries(snapshot).some(([, data]) => (
    /from\s+["']next\/font\/google["']/.test(fileText(data))
  ));
  return {
    supported: true,
    framework: "next-managed-runtime",
    packageManager: manager,
    installCommand: INSTALL_COMMANDS[manager],
    buildCommand: "./node_modules/.bin/next build --webpack",
    startCommand: "./node_modules/.bin/next start --hostname 0.0.0.0 --port 8080",
    port: 8080,
    connections,
    buildHosts: usesGoogleFonts ? ["fonts.googleapis.com", "fonts.gstatic.com"] : [],
    ...(access ? { access } : {}),
  };
}

function runtimeConnectionsFromDiscovery(discovery: ApiDiscoveryResult): SourceRuntimeConnection[] {
  if (!discovery.complete) return [];
  const sources = [...new Set(discovery.credentialSources
    .filter((source) => source.locus === "server" && source.confidence === "high")
    .map((source) => source.source))].sort();
  const connections: SourceRuntimeConnection[] = [];
  for (const environmentVariable of sources) {
    if (!/^[A-Z][A-Z0-9_]{0,199}$/.test(environmentVariable)) continue;
    const findings = discovery.findings.filter((finding) => finding.locus === "server"
      && finding.confidence === "high"
      && finding.credentials.some((credential) => credential.source === environmentVariable));
    if (findings.length === 0) continue;
    const hint = RUNTIME_CONNECTION_CATALOG[environmentVariable];
    const credential = runtimeCredentialFromDiscovery(environmentVariable, findings, hint);
    if (!credential) continue;
    const actions = findings.map((finding) => runtimeActionPolicy(finding, hint)).filter((value) => value !== undefined);
    if (actions.length !== findings.length) continue;
    const uniqueActions = [...new Map(actions.map((action) => [
      JSON.stringify(action),
      action,
    ])).values()].sort((left, right) => left.origin.localeCompare(right.origin)
      || left.path.localeCompare(right.path) || left.method.localeCompare(right.method));
    connections.push({
      alias: hint?.alias ?? runtimeAlias(environmentVariable),
      label: hint?.label ?? runtimeLabel(environmentVariable),
      environmentVariable,
      ...credential,
      hosts: [...new Set(uniqueActions.map((action) => new URL(action.origin).hostname))].sort(),
      actions: uniqueActions,
    });
  }
  return connections;
}

function runtimeCredentialFromDiscovery(
  environmentVariable: string,
  findings: readonly ApiDiscoveryFinding[],
  hint?: RuntimeConnectionCatalogEntry,
): Pick<SourceRuntimeConnection, "credential" | "credentialName"> | undefined {
  const evidence = findings.flatMap((finding) => finding.credentials
    .filter((credential) => credential.source === environmentVariable)
    .map((credential) => ({ finding, credential })));
  if (evidence.length === 0) return undefined;
  const signatures = new Set(evidence.map(({ credential }) => `${credential.placement}:${credential.transform}`));
  if (signatures.size !== 1) return undefined;
  const { credential } = evidence[0]!;
  if (hint?.credential === "webhook" && hint.hosts.length === 1
    && evidence.length === findings.length
    && credential.placement === "url" && credential.transform === "direct"
    && findings.every((finding) => !finding.request.origin && !finding.request.path)) return { credential: "webhook" };
  if (credential.placement === "header" && credential.transform === "bearer") return { credential: "bearer" };
  if (credential.placement === "header" && credential.transform === "basic") return { credential: "basic-empty-password" };
  if (credential.placement === "header" && credential.transform === "direct") {
    const name = uniqueCredentialName(evidence.map(({ finding }) => finding.request.headers), "header");
    return name ? { credential: "header", credentialName: name } : undefined;
  }
  if ((credential.placement === "query" || credential.placement === "url") && credential.transform === "direct") {
    const name = uniqueCredentialName(evidence.map(({ finding }) => finding.request.query), "query");
    return name ? { credential: "query", credentialName: name } : undefined;
  }
  return undefined;
}

function uniqueCredentialName(values: readonly string[][], kind: "header" | "query"): string | undefined {
  const names = new Set<string>();
  for (const entries of values) {
    if (entries.length !== 1) return undefined;
    const name = entries[0];
    if (!name || (kind === "header" ? !/^[!#$%&'*+.^_`|~0-9A-Za-z-]{1,128}$/.test(name) : !/^[A-Za-z0-9_.~-]{1,128}$/.test(name))) {
      return undefined;
    }
    const normalized = kind === "header" ? name.toLowerCase() : name;
    if (kind === "header" && ["connection", "content-length", "cookie", "host", "origin", "proxy-authorization",
      "referer", "set-cookie", "transfer-encoding"].includes(normalized)) return undefined;
    names.add(normalized);
  }
  return names.size === 1 ? [...names][0] : undefined;
}

function runtimeActionPolicy(
  finding: ApiDiscoveryFinding,
  hint?: RuntimeConnectionCatalogEntry,
): SourceRuntimeActionPolicy | undefined {
  const { method, origin, path } = finding.request;
  if (!/^(?:GET|HEAD|POST|PUT|PATCH|DELETE)$/.test(method)) return undefined;
  const webhook = !origin && !path && hint?.credential === "webhook" && hint.hosts.length === 1
    && finding.credentials.some((credential) => credential.placement === "url" && credential.transform === "direct");
  const parsed = publicRuntimeOrigin(origin ?? (webhook ? `https://${hint.hosts[0]}` : ""));
  const reviewedPath = path ?? (webhook ? "/.doorway/connection/{credential}" : "");
  if (!parsed || !safeRuntimePath(reviewedPath)) return undefined;
  const query = canonicalRuntimeNames(finding.request.query, "query");
  const headers = canonicalRuntimeNames([...new Set([
    ...finding.request.headers,
    ...(finding.request.body ? ["content-type"] : []),
  ])], "header");
  if (!query || !headers) return undefined;
  const body = finding.request.body;
  if (body?.mode === "unknown" || (body && (body.fields.length > 64
    || new Set(body.fields).size !== body.fields.length
    || body.fields.some((field) => field.length === 0 || field.length > 256)))) return undefined;
  return {
    method,
    origin: parsed.origin,
    path: reviewedPath,
    query,
    headers,
    ...(body ? { body: { mode: body.mode, fields: [...body.fields].sort() } } : {}),
  };
}

function canonicalRuntimeNames(values: readonly string[], kind: "header" | "query"): string[] | undefined {
  if (values.length > 64) return undefined;
  const names = values.map((value) => kind === "header" ? value.toLowerCase() : value).sort();
  if (new Set(names).size !== names.length || names.some((name) => kind === "header"
    ? !/^[!#$%&'*+.^_`|~0-9a-z-]{1,128}$/.test(name) || RUNTIME_STRIPPED_HEADERS.has(name)
    : !/^[A-Za-z0-9_.~-]{1,128}$/.test(name))) return undefined;
  return names;
}

function publicRuntimeOrigin(value: string): URL | undefined {
  let parsed: URL;
  try { parsed = new URL(value); } catch { return undefined; }
  const hostname = parsed.hostname.toLowerCase();
  const reserved = hostname === "localhost"
    || [".arpa", ".example", ".home", ".internal", ".invalid", ".lan", ".local", ".localdomain", ".localhost", ".onion", ".test"]
      .some((suffix) => hostname.endsWith(suffix));
  if (parsed.protocol !== "https:" || parsed.username || parsed.password || parsed.port
    || parsed.pathname !== "/" || parsed.search || parsed.hash || parsed.origin !== value
    || hostname.length === 0 || !hostname.includes(".") || hostname.endsWith(".")
    || /^(?:\d{1,3}\.){3}\d{1,3}$/.test(hostname) || hostname.includes(":") || reserved
    || hostname.split(".").some((label) => label.length === 0 || label.length > 63
      || !/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(label))) return undefined;
  return parsed;
}

function safeRuntimePath(value: string): boolean {
  if (!value.startsWith("/") || value.startsWith("//") || value.length > 2_048
    || /[\u0000-\u001f\u007f\\]/.test(value) || /%(?:2e|2f|5c)/i.test(value)) return false;
  const segments = value.split("/").slice(1);
  if (segments.length > 64) return false;
  let dynamicSegments = 0;
  for (const segment of segments) {
    if (!/[{}]/.test(segment)) continue;
    if (!/^\{[A-Za-z_$][A-Za-z0-9_$]{0,63}\}$/.test(segment)) return false;
    dynamicSegments += 1;
  }
  return dynamicSegments <= 8;
}

function runtimeAlias(environmentVariable: string): string {
  return environmentVariable.toLowerCase().replace(/_/g, "-").slice(0, 120);
}

function runtimeLabel(environmentVariable: string): string {
  return environmentVariable.toLowerCase().split("_").map((part) => part[0]?.toUpperCase() + part.slice(1)).join(" ");
}

function blockedRuntime(code: SourceRuntimeBlockCode, message: string): BlockedSourceRuntimePlan {
  return { supported: false, code, message };
}

function referencedEnvironment(snapshot: ProjectSnapshot): Set<string> {
  const names = new Set<string>();
  for (const [path, data] of stableRecordEntries(snapshot)) {
    if (!/\.(?:[cm]?[jt]sx?)$/.test(path)) continue;
    for (const match of fileText(data).matchAll(/\bprocess\.env\.([A-Z][A-Z0-9_]*)\b/g)) {
      if (match[1]) names.add(match[1]);
    }
  }
  return names;
}

function detectSignedCookieAccess(
  snapshot: ProjectSnapshot,
  environment: ReadonlySet<string>,
): SourceRuntimeAccessAdapter | undefined {
  if (!environment.has("APP_PASSWORD") || !environment.has("SESSION_SECRET")) return undefined;
  const text = stableRecordEntries(snapshot)
    .filter(([path]) => /\.(?:[cm]?[jt]sx?)$/.test(path))
    .map(([, data]) => fileText(data))
    .join("\n");
  const cookieName = text.match(/(?:COOKIE_NAME\s*=\s*|cookies?\.get\()\s*["']([^"']+)["']/)?.[1];
  if (!cookieName || !/createHmac\(\s*["']sha256["']/.test(text)) return undefined;
  return {
    type: "signed-cookie",
    cookieName,
    passwordEnvironmentVariable: "APP_PASSWORD",
    sessionSecretEnvironmentVariable: "SESSION_SECRET",
  };
}

export interface SourceUploadOptions {
  maxBytes?: number;
  maxFiles?: number;
  maxFileBytes?: number;
}

export function validateSourceUpload(
  input: readonly SnapshotFile[],
  options: SourceUploadOptions = {},
): SnapshotFile[] {
  const maxBytes = options.maxBytes ?? 5 * 1024 * 1024;
  const maxFiles = options.maxFiles ?? 1_000;
  const maxFileBytes = options.maxFileBytes ?? 1024 * 1024;
  for (const file of input) {
    if ((file.kind ?? "file") !== "file") {
      throw new SourceBuildError<SourceUploadCode>(
        "SOURCE_UNSAFE_FILE_TYPE",
        `${file.path} is not a regular source file.`,
      );
    }
    if (byteLength(file.data) > maxFileBytes) {
      throw new SourceBuildError<SourceUploadCode>(
        "SOURCE_FILE_TOO_LARGE",
        `${file.path} exceeds the source file limit.`,
      );
    }
  }

  const manifest = buildProjectManifest(
    input.map((file) => ({ path: file.path, size: byteLength(file.data) })),
    { maxBytes, maxFiles },
  );
  const included = new Set(manifest.included.map((file) => file.path));
  const files = input
    .filter((file) => included.has(file.path.normalize("NFC")))
    .filter((file) => {
      const hasHugoConfig = HUGO_CONFIG_FILENAMES.some((path) => included.has(path));
      return !hasHugoConfig || !file.path.normalize("NFC").startsWith("public/");
    })
    .map((file) => ({ ...file, data: cloneData(file.data) }))
    .sort((left, right) => left.path.localeCompare(right.path));
  const findings = scanArtifactForSecrets(files);
  if (findings.length > 0) {
    const paths = [...new Set(findings.map((finding) => finding.path))];
    throw new SourceBuildError<SourceUploadCode>(
      "SECRET_DETECTED",
      `Remove secret-like content before building: ${paths.join(", ")}`,
    );
  }
  return files;
}

function cloneData(data: FileData): FileData {
  return typeof data === "string" ? data : Uint8Array.from(data);
}
